import { Inbox, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useActionsCourrier, useCourriers, useEtatImportCourrier, useTableauDeBord } from '@/api/hooks';
import { LIBELLE_TYPE_DOCUMENT } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { PrioriteBadge, StatutCourrierBadge } from '@/components/courrier';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { dateFr, dateHeureFr } from '@/lib/utils';

export function CourrierListe() {
  const session = useSession();
  const u = session!.utilisateur;
  const tdb = useTableauDeBord();
  const assistante = u.profils.includes('ASSISTANT') || u.profils.includes('ADMINISTRATEUR');
  const conseiller = u.profils.includes('CONSEILLER_TECHNIQUE');
  const parapheur = Boolean(tdb.data?.courrier.parapheur);
  const [vue, setVue] = useState<'tous' | 'a-analyser' | 'parapheur'>(conseiller ? 'a-analyser' : 'tous');
  const [q, setQ] = useState('');
  const { data, isLoading, error } = useCourriers({ vue, q: q.trim() || undefined });
  const importation = useEtatImportCourrier(assistante);
  const actions = useActionsCourrier();
  const relever = async () => {
    try {
      const r = await actions.importer.mutateAsync();
      toast.success(r.crees ? `${r.crees} courrier(s) enregistré(s) depuis ${r.boite}.` : `Aucun nouveau courriel dans ${r.boite}.`, { description: r.erreurs.length ? `${r.erreurs.length} courriel(s) non importé(s) : ${r.erreurs.map((e) => e.message).join(', ')}` : r.dejaImportes ? `${r.dejaImportes} déjà importé(s).` : undefined });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Relève impossible.');
    }
  };

  return (
    <div>
      <PageHeader
        titre="Courrier entrant"
        sousTitre="Enregistrement par l'assistante, analyse du conseiller technique, décision et instructions du Directeur Général."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/instructions">Registre des instructions</Link>
            </Button>
            {assistante && importation.data?.configure && (
              <Button variant="outline" onClick={relever} disabled={actions.importer.isPending} title={`Boîte ${importation.data.boite}${importation.data.dernierImport ? ` · dernière relève ${dateHeureFr(importation.data.dernierImport.date)}` : ''}`}>
                <Inbox className="size-4" /> {actions.importer.isPending ? 'Relève en cours…' : 'Relever la boîte Microsoft 365'}
              </Button>
            )}
            {assistante && (
              <Button asChild>
                <Link to="/courrier/nouveau">
                  <Plus className="size-4" /> Enregistrer un courrier
                </Link>
              </Button>
            )}
          </>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={vue} onValueChange={(v) => setVue(v as typeof vue)}>
          <TabsList>
            {conseiller && <TabsTrigger value="a-analyser">À analyser</TabsTrigger>}
            {parapheur && <TabsTrigger value="parapheur">Parapheur</TabsTrigger>}
            <TabsTrigger value="tous">Tous</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input className="w-64" placeholder="Numéro, expéditeur, objet" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message="Aucun courrier." />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead className="hidden sm:table-cell">Reçu le</TableHead>
                <TableHead>Expéditeur</TableHead>
                <TableHead>Objet</TableHead>
                <TableHead className="hidden lg:table-cell">Conseiller</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Link to={`/courrier/${c.id}`} className="font-medium text-primary hover:underline">
                      {c.numero}
                    </Link>
                    <div className="text-xs text-muted-foreground">{LIBELLE_TYPE_DOCUMENT[c.typeDocument]}</div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{dateFr(c.dateReception)}</TableCell>
                  <TableCell className="max-w-[24ch] truncate">{c.expediteur}</TableCell>
                  <TableCell className="max-w-[36ch] truncate">
                    {c.confidentiel && <span className="mr-1 text-xs font-semibold text-red-700">Confidentiel ·</span>}
                    {c.objet}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">{c.conseiller?.nom ?? '—'}</TableCell>
                  <TableCell className="space-x-1">
                    <StatutCourrierBadge statut={c.statut} />
                    {c.priorite !== 'NORMALE' && <PrioriteBadge priorite={c.priorite} />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
